import { Injectable, PLATFORM_ID, Inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { TenantConfigService } from '../config/tenant-config';
import { OrderPayload, CartResponse, CreateFedexLabelPayload, OrderShippingPayload } from '../models/order-payload.model';
import { PLATFORM_API_URL } from '../config/api-config';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class OrderProcessingService {
    cartItemCount = signal<number>(0);
    isUploading = signal<boolean>(false);

    constructor(
        private http: HttpClient,
        private tenantConfig: TenantConfigService,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {
        if (isPlatformBrowser(this.platformId)) {
            this.updateCartCountFromStorage();
        }
    }

    private updateCartCountFromStorage() {
        try {
            const savedCart = localStorage.getItem('cart_response');
            if (savedCart) {
                const parsed: CartResponse = JSON.parse(savedCart);
                const count = parsed?.data?.cartItems?.length || 0;
                this.cartItemCount.set(count);
            } else {
                this.cartItemCount.set(0);
            }
        } catch (e) {
            console.error('Failed to parse cart_response from local storage', e);
        }
    }

    async addToCart(payload: OrderPayload): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return null;
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            // If the cart has existing items, retrieve the requestNumber from local storage to include it in the new payload
            try {
                const savedCart = localStorage.getItem('cart_response');
                if (savedCart) {
                    const parsed: CartResponse = JSON.parse(savedCart);
                    const cartItems = parsed?.data?.cartItems || [];
                    const cart_id = parsed?.data?.id;
                    if (cartItems.length > 0 && cart_id) {
                        payload.cart_id = cart_id;
                    }
                }
            } catch (e) {
                console.error('Failed to read requestNumber from local storage cart_response', e);
            }

            try {
                const response = await firstValueFrom(
                    this.http.post(`${PLATFORM_API_URL}/add-to-cart`, payload, { headers })
                );

                // Save to local storage
                payload.order_type == "Personal" ? null : localStorage.setItem('cart_response', JSON.stringify(response));
                localStorage.removeItem("shipping_response")
                localStorage.removeItem("full_order")
                localStorage.removeItem("payment_response")
                this.updateCartCountFromStorage();

                return response;
            } catch (error) {
                console.error('Error adding to cart:', error);
                throw error;
            }
        }
        return null;
    }

    async uploadFile(file: File): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                throw new Error('Website ID not found');
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            const formData = new FormData();
            formData.append('file', file);

            try {
                const response = await firstValueFrom(
                    this.http.post(`${PLATFORM_API_URL}/upload-user-doc-files`, formData, { headers })
                );
                return response;
            } catch (error) {
                console.error('Error uploading file:', error);
                throw error;
            }
        }
        return null;
    }

    async uploadShippingFile(file: File): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                throw new Error('Website ID not found');
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            const formData = new FormData();
            formData.append('file', file);

            try {
                const response = await firstValueFrom(
                    this.http.post(`${PLATFORM_API_URL}/upload-user-shipping-files`, formData, { headers })
                );
                return response;
            } catch (error) {
                console.error('Error uploading shipping file:', error);
                throw error;
            }
        }
        return null;
    }

    async submitOrderForm(payload: any): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return null;
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            try {
                const response = await firstValueFrom(
                    this.http.post(`${PLATFORM_API_URL}/order-form`, payload, { headers })
                );
                return response;
            } catch (error) {
                console.error('Error submitting order form:', error);
                throw error;
            }
        }
        return null;
    }

     async createShippingLabel(cartId: any, payload: CreateFedexLabelPayload, hideGlobalLoader: boolean = false): Promise<{success: boolean, data?: any, error?: any}> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return { success: false, error: 'Website ID not found' };
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            if (!hideGlobalLoader) {
                this.isUploading.set(true);
            }
            try {
                const response = await firstValueFrom(
                    this.http.post<any>(`${PLATFORM_API_URL}/create-fedex-label/${cartId}`, payload, { headers })
                );
                if (!hideGlobalLoader) {
                    this.isUploading.set(false);
                }
                return { success: true, data: response };
            } catch (error) {
                if (!hideGlobalLoader) {
                    this.isUploading.set(false);
                }
                console.error('Error creating shipping label:', error);
                return { success: false, error };
            }
        }
        return { success: false, error: 'Not in browser' };
    }

    async saveOrderShipping(cartId: any, payload: OrderShippingPayload): Promise<{success: boolean, data?: any, error?: any}> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return { success: false, error: 'Website ID not found' };
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            this.isUploading.set(true);
            try {
                const response = await firstValueFrom(
                    this.http.post<any>(`${PLATFORM_API_URL}/order-shipping/${cartId}`, payload, { headers })
                );
                this.isUploading.set(false);
                return { success: true, data: response };
            } catch (error) {
                this.isUploading.set(false);
                console.error('Error saving order shipping:', error);
                return { success: false, error };
            }
        }
        return { success: false, error: 'Not in browser' };
    }

    async deleteItemFromCart(cartItemId: number): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return null;
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            try {
                const response = await firstValueFrom(
                    this.http.delete<any>(`${PLATFORM_API_URL}/delete-item-from-cart/${cartItemId}`, { headers })
                );

                if (response) {
                    // Update localStorage with the response
                    if (response.data) {
                        localStorage.setItem('cart_response', JSON.stringify(response));
                    } else {
                        localStorage.setItem('cart_response', JSON.stringify({ data: response }));
                    }
                    this.updateCartCountFromStorage();
                }

                return response;
            } catch (error) {
                console.error('Error deleting item from cart:', error);
                throw error;
            }
        }
        return null;
    }

    async destroyCart(requestNumber: string): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return null;
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            try {
                const response = await firstValueFrom(
                    this.http.delete<any>(`${PLATFORM_API_URL}/destroy-cart/${requestNumber}`, { headers })
                );

                // Clear storage and update count
                localStorage.removeItem('cart_response');
                this.updateCartCountFromStorage();

                return response;
            } catch (error) {
                console.error('Error destroying cart:', error);
                throw error;
            }
        }
        return null;
    }

    async submitCartInvoicePayment(cartId: any, payload: {
        paymentToken: string;
        last4: string;
        customerName: string;
        email: string;
        zipCode: string;
    }): Promise<any> {
        if (isPlatformBrowser(this.platformId)) {
            const websiteId = this.tenantConfig.settings?.['websiteId'];
            if (!websiteId) {
                console.error('Website ID not found in tenant settings.');
                return null;
            }

            const headers = new HttpHeaders({
                'X-Tenant': websiteId.toString(),
            });

            try {
                const response = await firstValueFrom(
                    this.http.post<any>(`${PLATFORM_API_URL}/cart-invoice-payment/${cartId}`, payload, { headers })
                );
                return response;
            } catch (error) {
                console.error('Error submitting cart invoice payment:', error);
                throw error;
            }
        }
        return null;
    }

    async getAvailableCountries(): Promise<any[]> {
        try {
            const res = await firstValueFrom(
                this.http.get<any[]>(`${environment.fedexApiUrl}/api/fedex/available-countries`)
            );
            return res || [];
        } catch (error) {
            console.error('Failed to load countries', error);
            return [];
        }
    }

    async getCanadaStates(): Promise<any[]> {
        try {
            const res = await firstValueFrom(
                this.http.get<any[]>(`${environment.fedexApiUrl}/api/canada-states`)
            );
            return res || [];
        } catch (error) {
            console.error('Failed to load Canada states', error);
            return [];
        }
    }
}
